import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthEvent, OAuthService, OAuthSuccessEvent } from 'angular-oauth2-oidc';
import { OAuthConfig } from 'src/assets/authconfig/AuthConfig';
import { UtilityService } from '../common/utility.service';
import { UserProfileService } from '../UserAccount/userprofile.service';
import { IOAuthService } from './iOAuthService';

@Injectable({ providedIn: 'root' })

export class OAuthCodeFlowService extends IOAuthService {

  constructor(@Inject('IDENTITYSERVER_URL') identityBaseUrl: string,
    @Inject('BASE_URL') _apiURL: string,
    private oAuthService: OAuthService,
    public http: HttpClient,
    public userService: UserProfileService,
    public router: Router,
    public utility: UtilityService) {
    super(identityBaseUrl, utility, userService, router, http);

    this.configureService();
    this.oAuthService.events.subscribe(event => {
      if (event instanceof OAuthEvent) {
        //console.log("Event at ", new Date().toLocaleString(), ": ", event);
        if (event.type === 'token_expires') {
          const isTokenRefreshing = localStorage.getItem("isTokenRefreshing");
          if(isTokenRefreshing != "1")
            this.refreshToken();
        }
        // else if (event.type === 'token_received') {
        //   this.postReceivedToken()
        //   setTimeout(() => {
        //     localStorage.removeItem("isTokenRefreshing");
        //   }, 5000);
        // }
        else {
          setTimeout(() => {
            localStorage.removeItem("isTokenRefreshing");
          }, 5000);
        }
      }
    });
  }

  configureService() {
    //console.log('configureService called');
    const authConfig = { ...OAuthConfig };
    authConfig.issuer = `${this.identityBaseurl}${authConfig.issuer}`;
    authConfig.redirectUri = `${this.baseUrl}${authConfig.redirectUri}`;
    authConfig.postLogoutRedirectUri = `${this.baseUrl}${authConfig.postLogoutRedirectUri}`;
    // authConfig.silentRefreshRedirectUri = `${this.baseUrl}/refresh-token`;
    authConfig.responseType = 'code';
    authConfig.disablePKCE = false;
    authConfig.strictDiscoveryDocumentValidation = false;
    authConfig.showDebugInformation = true;

    if (this.subDomainName && !this.subDomainName.toLowerCase().includes('localhost')) {
      authConfig.clientId = this.subDomainName;
    }

    // Add this line to explicitly set storage to localStorage
    this.oAuthService.setStorage(localStorage);

    this.oAuthService.configure(authConfig);
    //this.oAuthService.setupAutomaticSilentRefresh();
  }

  async logOut() {
    //console.log('logOut called');
    localStorage.removeItem('currentUser');
    if (this.isAuthenticated()) {
      if (!this.oAuthService.discoveryDocumentLoaded) {
        await this.loadDiscoveryDocument();
      } else {
        this.oAuthService.logOut(false);
        this.removeSessionRelatedItems();
      }
    } else {
      await this.router.navigate(["callback"]);
    }
  }

  async authorizeAndLogin() {
    //console.log('authorizeAndLogin called');
    try {
      await this.oAuthService.loadDiscoveryDocumentAndLogin();

      if (this.isAuthenticated()) {
        await this.getUser();
      } else {
        this.oAuthService.initLoginFlow();
      }
    } catch (error) {
      console.error('Error during authorization and login', error);
      await this.router.navigate(['/error/500']);
    }
  }

  public async checkAndLoadDiscoveryDocument() {
    //console.log('checkAndLoadDiscoveryDocument called');
    if (!this.oAuthService.discoveryDocumentLoaded) {
      try {
        //console.log('loaded new discovery document called');
        await this.loadDiscoveryDocument();
        //this.copySessionRelatedItems_SessionToLocaltorage();
        if (!this.user && localStorage.getItem('currentUser')) {
          this.user = JSON.parse(localStorage.getItem('currentUser'));
          this.validatedSubDomain();
        }
      } catch (error) {
        console.error('Error in checkAndLoadDiscoveryDocument', error);
      }
    }
  }

  isAuthenticated(): boolean {
    //console.log('isAuthenticated called');
    return this.oAuthService.hasValidAccessToken();
  }

  public validatedSubDomain() {
    //console.log('validatedSubDomain called');
    if (this.isAuthenticated() && this.user != null) {
      if (this.user.subDomainName
        && !this.subDomainName.toLowerCase().includes("localhost")
        && this.subDomainName.toLowerCase() != this.user.subDomainName.toLowerCase()) {
        this.oAuthService.logOut(false);
      }
    }
  }

  public getAccessToken(): string {
    return localStorage.getItem('access_token') || this.oAuthService.getAccessToken();
  }

  private async loadDiscoveryDocument() {
    //console.log('loadDiscoveryDocument called');
    try {
      if (!this.oAuthService.discoveryDocumentLoaded) {
        await this.oAuthService.loadDiscoveryDocument();
        //this.copySessionRelatedItems_SessionToLocaltorage();
      }
    } catch (error) {
      console.error('Error loading discovery document', error);
      throw error;
    }
  }

  // Get the refresh token
  public getRefreshToken(): string {
    //console.log('getRefreshToken called');
    return this.oAuthService.getRefreshToken();
  }

  // Manually trigger a refresh
  public async refreshToken(): Promise<object> {
    //console.log('refreshToken called at', new Date().toLocaleString());

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
        console.error('No refresh token available');
        return Promise.reject('No refresh token available');
    }

    try {
        const result = await this.oAuthService.refreshToken();

        // Save the new token in localStorage
        const newAccessToken = this.oAuthService.getAccessToken();
        const newRefreshToken = this.oAuthService.getRefreshToken();

        //console.log('New Access Token:', newAccessToken);
        //console.log('New Refresh Token:', newRefreshToken);

        if (newAccessToken) {
            localStorage.setItem('access_token', newAccessToken);
        }
        if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
        }

        return result;
    } catch (error) {
        console.error('Error refreshing token:', error);
        return Promise.reject(error);
    }
}

  // Set up error handling for token refresh
  private setupTokenRefreshErrorHandling() {
    //console.log('setupTokenRefreshErrorHandling called');
    this.oAuthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuth error:', event);
        if (event.type === 'token_refresh_error') {
          //console.log('Refresh token error occurred. Redirecting to login...');
          this.removeSessionRelatedItems();
          this.router.navigate(['login']);
        }
      }
    });
  }
}
